// src/pages/StrategyEditPage.js
import React, { useCallback, useEffect, useMemo, useRef, useState, useDeferredValue } from 'react';
import { createPortal } from 'react-dom';
import './StrategyEditPage.css';
import TopBar from '../components/TopBar';
import TeamBadge from '../components/TeamBadge';
import Tooltip from '../components/Tooltip';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { ensureFantasyDataSynced, getPlayersFromFirestore } from '../firebase/playerService';
import { applyFantAppRoleOverrides } from '../utils/fantAppRoleOverrides';
import { getUserDisplayNamesByIds } from '../firebase/usersService';
import { RoleChip, fmt } from '../components/auction/auctionCommon';
import { DEFAULT_MANTRA_ROLE_CONFIGURATION, MANTRA_TOKEN_ORDER } from '../components/auction/MantraRoleConfiguration';
import { buildRoleOverrideMap, normalizeNameText } from '../components/auction/auctionRosterUtils';
import {
  getStrategyById,
  updateStrategyMeta,
  setStrategyRolesMode,
  saveStrategyPlayerCategory,
  saveStrategyPlayerTier,
  saveStrategyPlayerNote,
  saveStrategyPlayerMaxSpend,
  saveStrategyPlayerBackupGoalkeeper,
} from '../firebase/strategyService';
import { containsBlasphemy } from '../utils/blasphemyFilter';
import {
  ArrowLeft,
  Search,
  X,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  SlidersHorizontal,
  PencilLine,
  Tag,
  Award,
  StickyNote,
  Fish,
  Target,
  Bandage,
  Plane,
  PiggyBank,
  KeyRound,
  Dices,
  RotateCcw,
  Pencil,
  Sparkles,
  AlertTriangle,
  ExternalLink,
  Percent,
  Globe2,
  UserRound,
  Users,
  ShieldCheck,
} from 'lucide-react';
import useCustomCategoryLabel from '../hooks/useCustomCategoryLabel';

const PAGE_SIZE = 60;
const CLASSIC_ROLE_CHIPS = ['P', 'D', 'C', 'A'];
const MANTRA_ROLE_CHIPS = MANTRA_TOKEN_ORDER; // ['P','Dd','Dc','Ds','B','E','M','C','W','T','A','Pc']

const CATEGORY_FILTERS = ['all', 'none', 'trap', 'target', 'injury', 'internationalcup', 'cheap', 'key', 'wager', 'goodstarter', 'custom'];

const DEFAULT_CUSTOM_LABEL = 'Personalizzata';

const CATEGORY_META = {
  all:              { label: 'Tutte',          description: 'Mostra tutti i giocatori',                         icon: Tag },
  none:             { label: 'Nessuna',         description: 'Nessuna etichetta assegnata',                      icon: Tag },
  trap:             { label: 'Esca',            description: 'Giocatore da usare come esca in asta',             icon: Fish },
  target:           { label: 'Obiettivo',       description: 'Giocatore interessante, da tenere monitorato',     icon: Target },
  injury:           { label: 'Infortunio',      description: 'Giocatore soggetto ad infortuni',                  icon: Bandage },
  internationalcup: { label: 'Coppa Internaz.', description: 'Giocatore convocabile per competizioni internaz.', icon: Plane },
  cheap:            { label: 'Economico',       description: 'Costo inferiore al valore reale',                  icon: PiggyBank },
  key:              { label: 'Chiave',          description: 'Giocatore che deve essere tuo',                    icon: KeyRound },
  wager:            { label: 'Scommessa',       description: 'Potrebbe over-performare',                         icon: Dices },
  goodstarter:      { label: 'Buon titolare',   description: 'Titolare fisso nella sua squadra',                 icon: ShieldCheck },
  custom:           { label: DEFAULT_CUSTOM_LABEL, description: 'Etichetta personalizzata', icon: Sparkles },
};

const SORT_OPTIONS = [
  { value: 'value_desc',    label: 'Valore ↓' },
  { value: 'fv_desc',       label: 'FV ↓' },
  { value: 'mv_desc',       label: 'MV ↓' },
  { value: 'goals_desc',    label: 'Gol fatti ↓' },
  { value: 'conceded_desc', label: 'Gol subiti ↓' },
  { value: 'maxspend_desc', label: 'Tetto ↓' },
  { value: 'tier_asc',      label: 'Fascia' },
  { value: 'role_asc',      label: 'Ruolo' },
  { value: 'name',          label: 'Nome A-Z' },
];

// Ordine fisso richiesto per l'ordinamento "Ruolo": Portieri, Difensori,
// Centrocampisti, Attaccanti — non alfabetico.
const ROLE_SORT_ORDER = { P: 0, D: 1, C: 2, A: 3 };

const TIER_FILTERS = ['all', 'none', '1', '2', '3', '4', '5'];
const TIER_META = {
  none: { label: 'Nessuna',  description: 'Nessuna fascia assegnata' },
  1:    { label: 'Fascia 1', description: 'Livello massimo' },
  2:    { label: 'Fascia 2', description: 'Livello alto' },
  3:    { label: 'Fascia 3', description: 'Livello medio' },
  4:    { label: 'Fascia 4', description: 'Livello basso' },
  5:    { label: 'Fascia 5', description: 'Livello minimo' },
};

function getCategoryMeta(category = 'none', customLabel) {
  const meta = CATEGORY_META[category] || CATEGORY_META.none;
  if (category === 'custom' && customLabel && customLabel.trim()) {
    return { ...meta, label: customLabel.trim() };
  }
  return meta;
}
function getTierMeta(tier) {
  const n = Number(tier);
  return TIER_META[n >= 1 && n <= 5 ? n : 'none'];
}

function normalizeText(value = '') {
  return String(value)
    .toLowerCase()
    .trim()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

// Stessa catena di fallback usata per "Gol subiti" in AuctionMarketTab.js \u2014
// il nome del campo varia a seconda della fonte dati del giocatore.
function getGoalsConceded(player) {
  return player?.gotgoals ?? player?.goalsConceded ?? player?.goals_against ?? 0;
}

function CategoryChip({ category, showTooltip = true, customLabel }) {
  const meta = getCategoryMeta(category, customLabel);
  const Icon = meta.icon;
  return (
    <Tooltip as="span" className={`se-category-chip se-category-chip--${category}`} content={showTooltip ? meta.description : undefined}>
      <span className="se-category-chip__icon" aria-hidden="true">{Icon ? <Icon size={13} /> : null}</span>
      <span className="se-category-chip__label">{meta.label}</span>
    </Tooltip>
  );
}

// Stesso raggruppamento colore usato per i chip ruolo Mantra in AuctionMarketTab.js
function getMantraRoleGroup(token = '') {
  const upper = String(token || '').trim().toUpperCase();
  if (upper === 'P') return 'P';
  if (['DD', 'DC', 'DS', 'B'].includes(upper)) return 'D';
  if (['E', 'M', 'C', 'W', 'T'].includes(upper)) return 'C';
  if (['A', 'PC'].includes(upper)) return 'A';
  return 'A';
}

// Token del ruolo Mantra di un giocatore. DEFAULT_MANTRA_ROLE_CONFIGURATION non
// elenca i portieri: il loro ruolo Mantra coincide sempre col classic ("P"),
// quindi fallback esplicito — senza, il chip/filtro mostrava "—" per qualunque
// portiere non presente in quella lista curata.
function getPlayerMantraTokens(player, mantraRoleMap) {
  const classicRole = String(player?.role || '').trim().toUpperCase();
  const raw = mantraRoleMap?.[normalizeNameText(player?.name || '')] || (classicRole === 'P' ? 'P' : '');
  return raw ? raw.split('/').filter(Boolean) : [];
}

// Stessi chip (classi auction-role-chip*) già usati per il ruolo Mantra nel
// tab Market dell'asta: un chip colorato per ogni ruolo del giocatore.
function MantraRoleChip({ player, mantraRoleMap }) {
  const tokens = getPlayerMantraTokens(player, mantraRoleMap);

  if (!tokens.length) {
    return (
      <span className="auction-role-chip auction-role-chip--mantra">
        —
      </span>
    );
  }

  return (
    <span className="auction-role-badges" aria-label={tokens.join(' / ')}>
      {tokens.map((token, idx) => {
        const group = getMantraRoleGroup(token);
        return (
          <span
            key={`${token}-${group}-${idx}`}
            className={`auction-role-chip auction-role-chip--mantra auction-role-chip--mantra-${group}`}
          >
            {token}
          </span>
        );
      })}
    </span>
  );
}

function TierChip({ tier, showTooltip = true }) {
  const value = Number(tier) >= 1 && Number(tier) <= 5 ? Number(tier) : 0;
  const meta = getTierMeta(value);
  return (
    <Tooltip as="span" className={`se-tier-chip se-tier-chip--${value || 'none'}`} content={showTooltip ? meta.description : undefined}>
      <Award size={12} className="se-tier-chip__icon" aria-hidden="true" />
      <span className="se-tier-chip__label">{value ? `F${value}` : '—'}</span>
    </Tooltip>
  );
}

function MaxSpendChip({ percent, showTooltip = true }) {
  const value = Number(percent) > 0 ? Number(percent) : 0;
  return (
    <Tooltip as="span" className={`se-maxspend-chip ${value ? 'is-set' : ''}`} content={showTooltip ? (value ? `Tetto: ${value}% del budget dell'asta` : 'Nessun tetto impostato') : undefined}>
      <Percent size={12} className="se-maxspend-chip__icon" aria-hidden="true" />
      <span className="se-maxspend-chip__label">{value ? `${value}%` : '—'}</span>
    </Tooltip>
  );
}

// ─── POPOVER CATEGORIA/FASCIA ──────────────────────────────────────────────

function usePopoverStyle(anchorRect, width, estimatedH) {
  return useMemo(() => {
    if (!anchorRect || typeof window === 'undefined') return { top: 24, left: 24, width };
    const margin = 12;
    const vw = window.innerWidth, vh = window.innerHeight;
    let left = anchorRect.left;
    if (left + width > vw - margin) left = vw - width - margin;
    if (left < margin) left = margin;
    let top = anchorRect.bottom + 8;
    if (top + estimatedH > vh - margin) top = Math.max(margin, anchorRect.top - estimatedH - 8);
    return { top, left, width };
  }, [anchorRect, width, estimatedH]);
}

function usePopoverDismiss(open, onClose, ref) {
  useEffect(() => {
    if (!open) return;
    const down = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    const key = (e) => { if (e.key === 'Escape') onClose(); };
    // Il popover è in position:fixed (ancorato alla posizione del bottone
    // AL MOMENTO dell'apertura, non ricalcolata dopo): senza questo,
    // scorrendo la pagina o la tabella sotto resta fermo a schermo invece
    // di seguire il bottone che l'ha aperto, sembrando "staccato"/appiccicato
    // in giro per lo schermo. Chiude alla prima scrollata — tranne se lo
    // scroll avviene DENTRO al popover stesso (es. l'elenco lungo di un
    // filtro), quel caso non deve chiuderlo. `scroll` non fa bubbling ma
    // viene comunque intercettato in fase di capture da un listener più in
    // alto, quindi funziona anche per lo scroll interno di .se-table-wrap.
    const scroll = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', scroll, true);
    return () => {
      document.removeEventListener('pointerdown', down, true);
      document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', scroll, true);
    };
  }, [open, onClose, ref]);
}

function CategoryPopover({ open, anchorRect, playerName, currentCategory, onSelect, onClose, saving, customLabel, canRenameCustom = false, onRequestRenameCustom }) {
  const menuRef = useRef(null);
  const style = usePopoverStyle(anchorRect, 380, 340);
  usePopoverDismiss(open, onClose, menuRef);

  if (!open || typeof document === 'undefined') return null;

  const items = ['none', 'trap', 'target', 'injury', 'internationalcup', 'cheap', 'key', 'wager', 'goodstarter', 'custom'];

  return createPortal(
    <div className="se-picker-layer">
      <div ref={menuRef} className="se-picker glass" style={{ position: 'fixed', top: style.top, left: style.left, width: style.width }} role="dialog" aria-label={`Seleziona etichetta per ${playerName}`}>
        <div className="se-picker__head">
          <div>
            <strong>{playerName}</strong>
            <span>Seleziona un'etichetta</span>
          </div>
          <button type="button" className="se-picker__close" onClick={onClose} aria-label="Chiudi"><X size={16} /></button>
        </div>
        <div className="se-picker__list se-picker__list--grid">
          {items.map((category) => {
            const meta = getCategoryMeta(category, customLabel);
            const Icon = meta.icon;
            const active = category === currentCategory;

            return (
              <Tooltip
                as="button"
                key={category}
                type="button"
                className={['se-category-option', `se-category-option--${category}`, active ? 'is-active' : ''].filter(Boolean).join(' ')}
                onClick={() => onSelect(category)}
                disabled={saving}
                content={meta.description}
              >
                <span className="se-category-option__icon" aria-hidden="true">{Icon ? <Icon size={14} /> : null}</span>
                <span className="se-category-option__body">
                  <strong>{meta.label}</strong>
                  <small>{meta.description}</small>
                </span>
                {category === 'custom' && canRenameCustom && (
                  <span
                    className="se-category-option__rename"
                    role="button"
                    tabIndex={0}
                    aria-label="Rinomina etichetta personalizzata"
                    onClick={(e) => { e.stopPropagation(); onRequestRenameCustom?.(meta.label); }}
                  >
                    <PencilLine size={13} />
                  </span>
                )}
                {active && <span className="se-category-option__activeDot" />}
              </Tooltip>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── MODALE RINOMINA ETICHETTA "PERSONALIZZATA" ──────────────────────────────

function RenameCustomCategoryModal({ initialLabel, onClose, onSubmit, saving }) {
  const [value, setValue] = useState(initialLabel || '');
  const isValid = value.trim().length > 0;

  return (
    <div className="se-modal-backdrop" onClick={onClose}>
      <div className="se-modal glass" onClick={(e) => e.stopPropagation()}>
        <div className="se-modal__head">
          <div>
            <div className="se-modal__eyebrow"><Sparkles size={14} />Etichetta personalizzata</div>
            <h2>Rinomina l'etichetta</h2>
          </div>
          <button className="se-modal__close" onClick={onClose} type="button"><X size={18} /></button>
        </div>

        <form className="se-form" onSubmit={(e) => { e.preventDefault(); if (isValid) onSubmit(value.trim()); }}>
          <label>
            <span>Nome etichetta</span>
            <input value={value} onChange={(e) => setValue(e.target.value)} maxLength={24} autoFocus required />
          </label>
          <div className="se-form__actions">
            <button type="button" className="se-cancel-btn" onClick={onClose}>Annulla</button>
            <button className="se-save-btn" type="submit" disabled={saving || !isValid}>
              {saving ? <><Loader2 size={16} className="spin" /> Salvo...</> : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TierPopover({ open, anchorRect, playerName, currentTier, onSelect, onClose, saving }) {
  const menuRef = useRef(null);
  const style = usePopoverStyle(anchorRect, 260, 260);
  usePopoverDismiss(open, onClose, menuRef);

  if (!open || typeof document === 'undefined') return null;

  const items = [0, 1, 2, 3, 4, 5];

  return createPortal(
    <div className="se-picker-layer">
      <div ref={menuRef} className="se-picker glass" style={{ position: 'fixed', top: style.top, left: style.left, width: style.width }} role="dialog" aria-label={`Seleziona fascia per ${playerName}`}>
        <div className="se-picker__head">
          <div>
            <strong>{playerName}</strong>
            <span>Seleziona una fascia</span>
          </div>
          <button type="button" className="se-picker__close" onClick={onClose} aria-label="Chiudi"><X size={16} /></button>
        </div>
        <div className="se-picker__list se-picker__list--tiers">
          {items.map((tier) => {
            const meta = getTierMeta(tier);
            const active = tier === currentTier;
            return (
              <Tooltip
                as="button"
                key={tier}
                type="button"
                className={['se-tier-option', `se-tier-option--${tier || 'none'}`, active ? 'is-active' : ''].filter(Boolean).join(' ')}
                onClick={() => onSelect(tier)}
                disabled={saving}
                content={meta.description}
              >
                <strong>{tier ? `Fascia ${tier}` : 'Nessuna'}</strong>
                {active && <span className="se-category-option__activeDot" />}
              </Tooltip>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

function MaxSpendPopover({ open, anchorRect, playerName, currentPercent, onSelect, onClose, saving }) {
  const menuRef = useRef(null);
  const style = usePopoverStyle(anchorRect, 280, 220);
  usePopoverDismiss(open, onClose, menuRef);
  const [value, setValue] = useState('');

  useEffect(() => {
    if (open) setValue(currentPercent ? String(currentPercent) : '');
  }, [open, currentPercent]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="se-picker-layer">
      <div ref={menuRef} className="se-picker glass" style={{ position: 'fixed', top: style.top, left: style.left, width: style.width }} role="dialog" aria-label={`Imposta tetto massimo per ${playerName}`}>
        <div className="se-picker__head">
          <div>
            <strong>{playerName}</strong>
            <span>Tetto massimo di spesa</span>
          </div>
          <button type="button" className="se-picker__close" onClick={onClose} aria-label="Chiudi"><X size={16} /></button>
        </div>

        <label className="se-maxspend-field">
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            inputMode="decimal"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Es. 12"
            autoFocus
          />
          <span>% del budget</span>
        </label>
        <p className="se-maxspend-hint">
          Si converte nei crediti giusti in base al budget di ogni asta in cui importi questa strategia.
        </p>

        <div className="se-picker__actions">
          <button type="button" className="se-cancel-btn" onClick={() => onSelect(0)} disabled={saving}>
            Rimuovi
          </button>
          <button type="button" className="se-save-btn" onClick={() => onSelect(Number(value) || 0)} disabled={saving}>
            Salva
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Sostituisce i <select> nativi dei filtri (squadra, ruolo, etichetta,
// fascia, ordinamento): la lista di opzioni di un <select> è disegnata dal
// sistema operativo, senza nessun hook CSS per ricolorarne la scrollbar (a
// differenza di un normale div scrollabile) — stesso limite delle freccine
// native dei numeri (vedi NumberStepper in SimulaRosaPage.js). Qui risolto
// col minimo indispensabile: un semplice elenco a tendina, ancorato appena
// sotto il bottone che lo apre, niente intestazione/pulsante di chiusura
// (a differenza dei picker per-giocatore più sotto, che sono vere e proprie
// finestre con più campi) — un solo componente generico (options =
// [{ value, label }]) riusato per squadra/ruolo/etichetta/fascia/ordinamento.
function FilterSelectPopover({ open, anchorRect, options, current, onSelect, onClose }) {
  const menuRef = useRef(null);
  const width = Math.max(anchorRect?.width || 0, 180);
  const style = usePopoverStyle(anchorRect, width, 320);
  usePopoverDismiss(open, onClose, menuRef);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div className="se-picker-layer">
      <div ref={menuRef} className="se-filter-dropdown glass" style={{ position: 'fixed', top: style.top, left: style.left, width: style.width }} role="listbox">
        <div className="se-filter-picker__list">
          {options.map(({ value, label }) => {
            const active = value === current;
            return (
              <button
                key={value}
                type="button"
                role="option"
                aria-selected={active}
                className={`se-filter-option ${active ? 'is-active' : ''}`}
                onClick={() => onSelect(value)}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── RIGA GIOCATORE ─────────────────────────────────────────────────────────

function PlayerRow({ player, category, tier, note, maxSpend, saving, savingTier, savingMaxSpend, mantraRoleMap, rolesMode = 'classic', readOnly = false, backupMode = false, backupValue = '', teamGoalkeepers = [], customCategoryLabel, onBackupGoalkeeperChange, onOpenCategoryPicker, onOpenTierPicker, onOpenMaxSpendPicker, onOpenNotesEditor, onOpenDetail }) {
  const hasNote = Boolean(String(note || '').trim());
  const role = String(player.role || '').trim().toUpperCase() || 'A';

  return (
    <tr className="se-row">
      <td className="se-td se-td--team"><TeamBadge team={player.team} size={30} /></td>
      <td className="se-td se-td--player">
        <div className="se-player-cell">
          <strong className="se-player-name">{player.name}</strong>
        </div>
      </td>
      <td className="se-td se-td--role">
        {rolesMode === 'mantra'
          ? <MantraRoleChip player={player} mantraRoleMap={mantraRoleMap} />
          : <RoleChip role={role} />}
      </td>
      {backupMode ? (
        <td className="se-td se-td--goals" onClick={(e) => e.stopPropagation()}>
          {readOnly ? (
            backupValue || '—'
          ) : (
            <>
              <input
                type="text"
                list={`se-backup-gk-${player.id}`}
                className="se-backup-input"
                defaultValue={backupValue}
                placeholder="Nome portiere..."
                onBlur={(e) => onBackupGoalkeeperChange(player.id, e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              />
              <datalist id={`se-backup-gk-${player.id}`}>
                {teamGoalkeepers.filter((gk) => String(gk.id) !== String(player.id)).map((gk) => (
                  <option key={gk.id} value={gk.name} />
                ))}
              </datalist>
            </>
          )}
        </td>
      ) : (
        <td className="se-td se-td--goals">{player.goals !== null && player.goals !== undefined ? fmt(player.goals) : '—'}</td>
      )}
      <td className="se-td se-td--assists">{player.assists !== null && player.assists !== undefined ? fmt(player.assists) : '—'}</td>
      <td className="se-td se-td--value">{player.value !== null && player.value !== undefined ? fmt(player.value) : '—'}</td>
      <td className="se-td se-td--fv">{player.fmarkavg !== null && player.fmarkavg !== undefined ? fmt(player.fmarkavg) : '—'}</td>
      <td className="se-td se-td--mv">{player.markavg !== null && player.markavg !== undefined ? fmt(player.markavg) : '—'}</td>

      {readOnly ? (
        <td className="se-td se-td--category"><CategoryChip category={category} customLabel={customCategoryLabel} /></td>
      ) : (
        <td className="se-td se-td--category" onClick={(e) => e.stopPropagation()}>
          <Tooltip as="button" type="button" className={`se-category-btn ${category !== 'none' ? 'is-tagged' : ''}`} onClick={(e) => onOpenCategoryPicker(player, e.currentTarget)} disabled={saving} content="Cambia etichetta">
            <CategoryChip category={category} showTooltip={false} customLabel={customCategoryLabel} />
            {saving && <Loader2 size={12} className="se-spinner" />}
          </Tooltip>
        </td>
      )}

      {readOnly ? (
        <td className="se-td se-td--tier"><TierChip tier={tier} /></td>
      ) : (
        <td className="se-td se-td--tier" onClick={(e) => e.stopPropagation()}>
          <Tooltip as="button" type="button" className={`se-tier-btn ${tier ? 'is-tagged' : ''}`} onClick={(e) => onOpenTierPicker(player, e.currentTarget)} disabled={savingTier} content="Cambia fascia">
            <TierChip tier={tier} showTooltip={false} />
            {savingTier && <Loader2 size={12} className="se-spinner" />}
          </Tooltip>
        </td>
      )}

      {readOnly ? (
        <td className="se-td se-td--maxspend"><MaxSpendChip percent={maxSpend} /></td>
      ) : (
        <td className="se-td se-td--maxspend" onClick={(e) => e.stopPropagation()}>
          <Tooltip as="button" type="button" className={`se-maxspend-btn ${maxSpend ? 'is-tagged' : ''}`} onClick={(e) => onOpenMaxSpendPicker(player, e.currentTarget)} disabled={savingMaxSpend} content="Imposta tetto massimo di spesa">
            <MaxSpendChip percent={maxSpend} showTooltip={false} />
            {savingMaxSpend && <Loader2 size={12} className="se-spinner" />}
          </Tooltip>
        </td>
      )}

      <td className="se-td se-td--note" onClick={(e) => e.stopPropagation()}>
        {readOnly ? (
          hasNote && (
            <Tooltip as="span" className="se-action-btn is-tagged" content={note}>
              <PencilLine size={15} />
            </Tooltip>
          )
        ) : (
          <Tooltip as="button" type="button" className={`se-action-btn ${hasNote ? 'is-tagged' : ''}`} onClick={() => onOpenNotesEditor(player)} content={hasNote ? 'Modifica nota' : 'Aggiungi nota'}>
            <PencilLine size={15} />
          </Tooltip>
        )}
      </td>

      <td className="se-td se-td--detail se-td--sticky-right" onClick={(e) => e.stopPropagation()}>
        <Tooltip as="button" type="button" className="se-action-btn" onClick={() => onOpenDetail(player.id)} content="Scheda giocatore">
          <ExternalLink size={15} />
        </Tooltip>
      </td>
    </tr>
  );
}

function MobilePlayerCard({ player, category, tier, note, maxSpend, saving, savingTier, savingMaxSpend, mantraRoleMap, rolesMode = 'classic', readOnly = false, backupMode = false, backupValue = '', teamGoalkeepers = [], customCategoryLabel, onBackupGoalkeeperChange, onOpenCategoryPicker, onOpenTierPicker, onOpenMaxSpendPicker, onOpenNotesEditor, onOpenDetail }) {
  const hasNote = Boolean(String(note || '').trim());
  const role = String(player.role || '').trim().toUpperCase() || 'A';

  return (
    <article className="sem-card">
      <div className="sem-card__top">
        <TeamBadge team={player.team} size={22} />
        <span className="sem-card__role">
          {rolesMode === 'mantra'
            ? <MantraRoleChip player={player} mantraRoleMap={mantraRoleMap} />
            : <RoleChip role={role} />}
        </span>
        <Tooltip as="strong" className="sem-card__name" content={player.name}>{player.name}</Tooltip>
        <div className="sem-card__stat"><span>MV</span><strong>{player.markavg !== null && player.markavg !== undefined ? fmt(player.markavg) : '—'}</strong></div>
        <div className="sem-card__stat"><span>FV</span><strong>{player.fmarkavg !== null && player.fmarkavg !== undefined ? fmt(player.fmarkavg) : '—'}</strong></div>
      </div>

      {backupMode && (
        <div className="sem-card__backup" onClick={(e) => e.stopPropagation()}>
          <span>Por riserva</span>
          {readOnly ? (
            <strong>{backupValue || '—'}</strong>
          ) : (
            <>
              <input
                type="text"
                list={`sem-backup-gk-${player.id}`}
                className="se-backup-input"
                defaultValue={backupValue}
                placeholder="Nome portiere..."
                onBlur={(e) => onBackupGoalkeeperChange(player.id, e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              />
              <datalist id={`sem-backup-gk-${player.id}`}>
                {teamGoalkeepers.filter((gk) => String(gk.id) !== String(player.id)).map((gk) => (
                  <option key={gk.id} value={gk.name} />
                ))}
              </datalist>
            </>
          )}
        </div>
      )}

      <div className="sem-card__controls" onClick={(e) => e.stopPropagation()}>
        <div className="sem-card__cat">
          {readOnly ? <CategoryChip category={category} customLabel={customCategoryLabel} /> : (
            <button type="button" className={`se-category-btn ${category !== 'none' ? 'is-tagged' : ''}`} onClick={(e) => onOpenCategoryPicker(player, e.currentTarget)} disabled={saving}>
              <CategoryChip category={category} customLabel={customCategoryLabel} />
              {saving && <Loader2 size={12} className="se-spinner" />}
            </button>
          )}
        </div>

        <div className="sem-card__tier">
          {readOnly ? <TierChip tier={tier} /> : (
            <button type="button" className={`se-tier-btn ${tier ? 'is-tagged' : ''}`} onClick={(e) => onOpenTierPicker(player, e.currentTarget)} disabled={savingTier}>
              <TierChip tier={tier} />
              {savingTier && <Loader2 size={12} className="se-spinner" />}
            </button>
          )}
        </div>

        <div className="sem-card__maxspend">
          {readOnly ? <MaxSpendChip percent={maxSpend} /> : (
            <button type="button" className={`se-maxspend-btn ${maxSpend ? 'is-tagged' : ''}`} onClick={(e) => onOpenMaxSpendPicker(player, e.currentTarget)} disabled={savingMaxSpend}>
              <MaxSpendChip percent={maxSpend} />
              {savingMaxSpend && <Loader2 size={12} className="se-spinner" />}
            </button>
          )}
        </div>

        <div className="sem-card__actions">
          {readOnly ? (
            hasNote && (
              <Tooltip as="span" className="sem-btn is-tagged" content={note}>
                <PencilLine size={13} />
              </Tooltip>
            )
          ) : (
            <Tooltip as="button" type="button" className={`sem-btn ${hasNote ? 'is-tagged' : ''}`} onClick={() => onOpenNotesEditor(player)} content={hasNote ? 'Modifica nota' : 'Aggiungi nota'}>
              <PencilLine size={13} />
            </Tooltip>
          )}
          <Tooltip as="button" type="button" className="sem-btn" onClick={() => onOpenDetail(player.id)} content="Scheda giocatore">
            <ExternalLink size={13} />
          </Tooltip>
        </div>
      </div>
    </article>
  );
}

// ─── MODALE RINOMINA ────────────────────────────────────────────────────────

function RenameModal({ initialName, initialDescription, onClose, onSubmit, saving }) {
  const [name, setName] = useState(initialName || '');
  const [description, setDescription] = useState(initialDescription || '');
  const isBlasphemous = containsBlasphemy(name);
  const isValid = name.trim().length > 0 && !isBlasphemous;

  return (
    <div className="se-modal-backdrop" onClick={onClose}>
      <div className="se-modal glass" onClick={(e) => e.stopPropagation()}>
        <div className="se-modal__head">
          <div>
            <div className="se-modal__eyebrow"><Sparkles size={14} />Modifica strategia</div>
            <h2>Nome e descrizione</h2>
          </div>
          <button className="se-modal__close" onClick={onClose} type="button"><X size={18} /></button>
        </div>

        <form className="se-form" onSubmit={(e) => { e.preventDefault(); if (isValid) onSubmit({ name: name.trim(), description: description.trim() }); }}>
          <label className={isBlasphemous ? 'is-invalid' : ''}>
            <span>Nome strategia</span>
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus required />
            {isBlasphemous && <small className="se-form__error">Nome non consentito.</small>}
          </label>
          <label>
            <span>Descrizione (opzionale)</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </label>
          <div className="se-form__actions">
            <button type="button" className="se-cancel-btn" onClick={onClose}>Annulla</button>
            <button className="se-save-btn" type="submit" disabled={saving || !isValid}>
              {saving ? <><Loader2 size={16} className="spin" /> Salvo...</> : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── PAGINA ─────────────────────────────────────────────────────────────────

export default function StrategyEditPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { strategyId, ownerUid } = useParams();

  // Presenza di :ownerUid in rotta = si sta guardando la strategia di un altro
  // utente (/strategie/pubblica/:ownerUid/:strategyId): può essere una
  // strategia PUBBLICA (sola lettura) oppure una CONDIVISA con noi. In
  // quest'ultimo caso, se il nostro uid è in `sharedWith`, possiamo anche
  // modificarla come il proprietario (le scritture vanno comunque su
  // users/{ownerUid}/strategies/..., cioè `effectiveUid`).
  const isForeignRoute = Boolean(ownerUid);
  const effectiveUid = isForeignRoute ? ownerUid : user?.uid;
  const [sharedCanEdit, setSharedCanEdit] = useState(false);
  const isReadOnly = isForeignRoute && !sharedCanEdit;

  // Nome dell'etichetta "Personalizzata": account-level (users/{effectiveUid}),
  // rinominabile solo dal proprietario — non da chi la vede tramite condivisione.
  const [customCategoryLabel, renameCustomCategoryLabel] = useCustomCategoryLabel(effectiveUid);
  const canRenameCustomCategory = !isForeignRoute;
  const [renamingCustomCategory, setRenamingCustomCategory] = useState(false);
  const [savingCustomCategoryLabel, setSavingCustomCategoryLabel] = useState(false);

  const openRenameCustomCategory = useCallback(() => {
    setPickerState(null);
    setRenamingCustomCategory(true);
  }, []);

  const submitRenameCustomCategory = useCallback(async (value) => {
    setSavingCustomCategoryLabel(true);
    try {
      await renameCustomCategoryLabel(value);
      setRenamingCustomCategory(false);
    } finally {
      setSavingCustomCategoryLabel(false);
    }
  }, [renameCustomCategoryLabel]);

  const [loading, setLoading] = useState(true);
  const [strategy, setStrategy] = useState(null);
  const [players, setPlayers] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [ownerName, setOwnerName] = useState('');

  // Stessa fonte e stesso meccanismo di lookup (per-nome esatto, senza
  // fallback su solo cognome) già usato dall'asta Mantra per applicare
  // DEFAULT_MANTRA_ROLE_CONFIGURATION ai giocatori — evita le collisioni tra
  // omonimi (es. i due Thuram) che il fallback "solo cognome" causerebbe.
  const mantraRoleMap = useMemo(
    () => buildRoleOverrideMap({ roleOverrides: DEFAULT_MANTRA_ROLE_CONFIGURATION }),
    []
  );

  const [localCats, setLocalCats] = useState({});
  const [localTiers, setLocalTiers] = useState({});
  const [localNotes, setLocalNotes] = useState({});
  const [localMaxSpends, setLocalMaxSpends] = useState({});
  const [localBackupGoalkeepers, setLocalBackupGoalkeepers] = useState({});

  const [savingId, setSavingId] = useState(null);
  const [savingTierId, setSavingTierId] = useState(null);
  const [savingMaxSpendId, setSavingMaxSpendId] = useState(null);
  const [showRename, setShowRename] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);

  // Modalità ruoli della strategia (classic | mantra): la tabella mostra la
  // colonna "Ruolo" col ruolo classic oppure Mantra, e il filtro ruolo usa i
  // chip corrispondenti. Persistita sul documento strategia.
  const [rolesMode, setRolesMode] = useState('classic');
  const [savingRolesMode, setSavingRolesMode] = useState(false);
  const isMantra = rolesMode === 'mantra';

  const [search, setSearch] = useState('');
  const dSearch = useDeferredValue(search);
  // Filtro ruolo multi-selezione: array di ruoli/token; [] = tutti.
  const [roleFilter, setRoleFilter] = useState([]);
  const [teamFilter, setTeamFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all');
  const [sortMode, setSortMode] = useState('value_desc');
  const [page, setPage] = useState(1);

  // Colonna "Por riserva" (sostituisce "Gol"): solo quando il filtro ruolo è
  // esattamente "P" — così ogni riga visibile è già un portiere.
  const backupMode = roleFilter.length === 1 && roleFilter[0] === 'P';

  const [pickerState, setPickerState] = useState(null);
  const [tierPickerState, setTierPickerState] = useState(null);
  const [maxSpendPickerState, setMaxSpendPickerState] = useState(null);
  const [notesState, setNotesState] = useState(null);
  // { type: 'team'|'category'|'tier'|'sort', anchorRect } oppure null
  // (il ruolo usa dei chip, non il popover)
  const [filterPickerState, setFilterPickerState] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false); // solo mobile: pannello filtri a scomparsa

  useEffect(() => {
    if (!user?.uid || !strategyId || !effectiveUid) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const [strategyData, playersList] = await Promise.all([
          getStrategyById(effectiveUid, strategyId),
          (async () => {
            let list = await getPlayersFromFirestore().catch(() => []);
            if (!list.length) {
              await ensureFantasyDataSynced();
              list = await getPlayersFromFirestore().catch(() => []);
            }
            return applyFantAppRoleOverrides(list);
          })(),
        ]);

        if (cancelled) return;

        if (!strategyData) {
          setNotFound(true);
          return;
        }

        setStrategy(strategyData);
        setRolesMode(strategyData.rolesMode === 'mantra' ? 'mantra' : 'classic');
        setPlayers(playersList);
        setLocalCats({ ...(strategyData.categories || {}) });
        setLocalTiers({ ...(strategyData.tiers || {}) });
        setLocalNotes({ ...(strategyData.notes || {}) });
        setLocalMaxSpends({ ...(strategyData.maxSpends || {}) });
        setLocalBackupGoalkeepers({ ...(strategyData.backupGoalkeepers || {}) });

        // Strategia altrui: se il nostro uid è tra i condivisi possiamo
        // modificarla (le regole Firestore consentono l'update ai condivisi).
        const canEdit = isForeignRoute
          && Array.isArray(strategyData.sharedWith)
          && strategyData.sharedWith.map(String).includes(String(user.uid));
        if (!cancelled) setSharedCanEdit(canEdit);

        if (isForeignRoute) {
          const names = await getUserDisplayNamesByIds([effectiveUid]).catch(() => ({}));
          if (!cancelled) setOwnerName(names[effectiveUid] || 'Utente');
        }
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.uid, strategyId, effectiveUid, isForeignRoute]);

  useEffect(() => { setPage(1); }, [dSearch, roleFilter, teamFilter, catFilter, tierFilter, sortMode, rolesMode]);

  const teamOptions = useMemo(() => {
    const s = new Set();
    players.forEach((p) => { if (p.team) s.add(p.team); });
    return ['all', ...Array.from(s).sort((a, b) => a.localeCompare(b))];
  }, [players]);

  // Portieri raggruppati per squadra: usato per suggerire, riga per riga,
  // solo portieri della stessa squadra come possibile riserva (mai se
  // stesso). Colonna "Por riserva" visibile solo filtrando per ruolo P.
  const goalkeepersByTeam = useMemo(() => {
    const map = {};
    players.forEach((p) => {
      if (String(p.role || '').trim().toUpperCase() !== 'P') return;
      const team = p.team || '';
      if (!map[team]) map[team] = [];
      map[team].push(p);
    });
    return map;
  }, [players]);

  const filtered = useMemo(() => {
    let list = [...players];

    if (dSearch.trim()) {
      const s = normalizeText(dSearch);
      list = list.filter((p) => normalizeText(p.name || '').startsWith(s) || normalizeText(p.team || '').startsWith(s));
    }
    if (roleFilter.length) {
      list = isMantra
        ? list.filter((p) => getPlayerMantraTokens(p, mantraRoleMap).some((t) => roleFilter.includes(t)))
        : list.filter((p) => roleFilter.includes(String(p.role || '').trim().toUpperCase()));
    }
    if (teamFilter !== 'all') list = list.filter((p) => String(p.team || '') === teamFilter);
    if (catFilter !== 'all') list = list.filter((p) => String(localCats?.[String(p.id)] || 'none') === catFilter);
    if (tierFilter !== 'all') {
      const wantTier = tierFilter === 'none' ? 0 : Number(tierFilter);
      list = list.filter((p) => Number(localTiers?.[String(p.id)] || 0) === wantTier);
    }

    const sorters = {
      name: (a, b) => a.name.localeCompare(b.name),
      value_desc: (a, b) => Number(b.value || 0) - Number(a.value || 0),
      fv_desc: (a, b) => Number(b.fmarkavg || 0) - Number(a.fmarkavg || 0),
      mv_desc: (a, b) => Number(b.markavg || 0) - Number(a.markavg || 0),
      tier_asc: (a, b) => {
        const ta = Number(localTiers?.[String(a.id)] || 0) || 6;
        const tb = Number(localTiers?.[String(b.id)] || 0) || 6;
        return ta - tb;
      },
      goals_desc: (a, b) => Number(b.goals || 0) - Number(a.goals || 0),
      conceded_desc: (a, b) => Number(getGoalsConceded(b)) - Number(getGoalsConceded(a)),
      maxspend_desc: (a, b) => Number(localMaxSpends?.[String(b.id)] || 0) - Number(localMaxSpends?.[String(a.id)] || 0),
      role_asc: (a, b) => {
        const ra = ROLE_SORT_ORDER[String(a.role || '').trim().toUpperCase()] ?? 4;
        const rb = ROLE_SORT_ORDER[String(b.role || '').trim().toUpperCase()] ?? 4;
        return ra - rb;
      },
    };
    const sorter = sorters[sortMode] || sorters.value_desc;
    return [...list].sort((a, b) => sorter(a, b) || sorters.value_desc(a, b));
  }, [players, dSearch, roleFilter, teamFilter, catFilter, tierFilter, sortMode, isMantra, mantraRoleMap, localCats, localTiers, localMaxSpends]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  const summary = useMemo(() => {
    const taggedPlayers = new Set([
      ...Object.keys(localCats || {}),
      ...Object.keys(localTiers || {}),
      ...Object.keys(localNotes || {}),
      ...Object.keys(localMaxSpends || {}),
    ]);
    return {
      categories: Object.keys(localCats || {}).length,
      tiers: Object.keys(localTiers || {}).length,
      notes: Object.keys(localNotes || {}).length,
      maxSpends: Object.keys(localMaxSpends || {}).length,
      players: taggedPlayers.size,
    };
  }, [localCats, localTiers, localNotes, localMaxSpends]);

  const openCategoryPicker = useCallback((player, el) => {
    const rect = el?.getBoundingClientRect?.() || null;
    setPickerState({ playerId: String(player.id), playerName: player.name || '—', currentCategory: String(localCats?.[String(player.id)] || 'none'), anchorRect: rect });
  }, [localCats]);

  const handleCategorySelect = useCallback(async (playerId, next) => {
    const key = String(playerId);
    const prev = String(localCats?.[key] || 'none');
    const normalized = String(next || 'none');
    setLocalCats((c) => {
      const copy = { ...c };
      if (normalized === 'none') delete copy[key]; else copy[key] = normalized;
      return copy;
    });
    setPickerState(null);
    setSavingId(key);
    try {
      await saveStrategyPlayerCategory(effectiveUid, strategyId, key, normalized);
    } catch {
      setLocalCats((c) => ({ ...c, [key]: prev }));
    } finally {
      setSavingId(null);
    }
  }, [localCats, effectiveUid, strategyId]);

  const openTierPicker = useCallback((player, el) => {
    const rect = el?.getBoundingClientRect?.() || null;
    setTierPickerState({ playerId: String(player.id), playerName: player.name || '—', currentTier: Number(localTiers?.[String(player.id)] || 0), anchorRect: rect });
  }, [localTiers]);

  const handleTierSelect = useCallback(async (playerId, next) => {
    const key = String(playerId);
    const prev = Number(localTiers?.[key] || 0);
    const normalized = Number(next) >= 1 && Number(next) <= 5 ? Number(next) : 0;
    setLocalTiers((t) => {
      const copy = { ...t };
      if (normalized === 0) delete copy[key]; else copy[key] = normalized;
      return copy;
    });
    setTierPickerState(null);
    setSavingTierId(key);
    try {
      await saveStrategyPlayerTier(effectiveUid, strategyId, key, normalized);
    } catch {
      setLocalTiers((t) => ({ ...t, [key]: prev }));
    } finally {
      setSavingTierId(null);
    }
  }, [localTiers, effectiveUid, strategyId]);

  const openMaxSpendPicker = useCallback((player, el) => {
    const rect = el?.getBoundingClientRect?.() || null;
    setMaxSpendPickerState({ playerId: String(player.id), playerName: player.name || '—', currentPercent: Number(localMaxSpends?.[String(player.id)] || 0), anchorRect: rect });
  }, [localMaxSpends]);

  const handleMaxSpendSelect = useCallback(async (playerId, next) => {
    const key = String(playerId);
    const prev = Number(localMaxSpends?.[key] || 0);
    const normalized = Math.min(Math.max(Math.round((Number(next) || 0) * 10) / 10, 0), 100);
    setLocalMaxSpends((m) => {
      const copy = { ...m };
      if (normalized === 0) delete copy[key]; else copy[key] = normalized;
      return copy;
    });
    setMaxSpendPickerState(null);
    setSavingMaxSpendId(key);
    try {
      await saveStrategyPlayerMaxSpend(effectiveUid, strategyId, key, normalized);
    } catch {
      setLocalMaxSpends((m) => ({ ...m, [key]: prev }));
    } finally {
      setSavingMaxSpendId(null);
    }
  }, [localMaxSpends, effectiveUid, strategyId]);

  const openNotes = useCallback((player) => {
    setNotesState({ playerId: String(player.id), playerName: player.name || '—', value: String(localNotes?.[String(player.id)] || '') });
  }, [localNotes]);

  const saveNotes = useCallback(async (value) => {
    if (!notesState?.playerId) return;
    const key = notesState.playerId;
    const normalized = String(value || '').trim();
    setLocalNotes((n) => {
      const copy = { ...n };
      if (normalized) copy[key] = normalized; else delete copy[key];
      return copy;
    });
    setNotesState(null);
    await saveStrategyPlayerNote(effectiveUid, strategyId, key, normalized);
  }, [notesState, effectiveUid, strategyId]);

  // Commit diretto onBlur (niente popover/modale, coerente con un campo
  // testuale libero con autocomplete): aggiorna subito lo stato locale,
  // salva in background.
  const saveBackupGoalkeeper = useCallback(async (playerId, value) => {
    const key = String(playerId);
    const normalized = String(value || '').trim();
    setLocalBackupGoalkeepers((prev) => {
      const copy = { ...prev };
      if (normalized) copy[key] = normalized; else delete copy[key];
      return copy;
    });
    await saveStrategyPlayerBackupGoalkeeper(effectiveUid, strategyId, key, normalized);
  }, [effectiveUid, strategyId]);

  const handleRolesModeChange = useCallback(async (next) => {
    if (isReadOnly || savingRolesMode) return;
    const normalized = next === 'mantra' ? 'mantra' : 'classic';
    if (normalized === rolesMode) return;
    const prev = rolesMode;
    setRolesMode(normalized);
    setRoleFilter([]); // i token Mantra non sono validi in classic e viceversa
    setStrategy((s) => (s ? { ...s, rolesMode: normalized } : s));
    setSavingRolesMode(true);
    try {
      await setStrategyRolesMode(effectiveUid, strategyId, normalized);
    } catch {
      setRolesMode(prev);
      setStrategy((s) => (s ? { ...s, rolesMode: prev } : s));
    } finally {
      setSavingRolesMode(false);
    }
  }, [isReadOnly, savingRolesMode, rolesMode, effectiveUid, strategyId]);

  const handleRenameSubmit = useCallback(async ({ name, description }) => {
    setSavingMeta(true);
    try {
      await updateStrategyMeta(effectiveUid, strategy, { name, description });
      setStrategy((prev) => ({ ...prev, name, description }));
      setShowRename(false);
    } finally {
      setSavingMeta(false);
    }
  }, [effectiveUid, strategy]);

  const openPlayerDetail = useCallback((playerId) => {
    window.open(`${window.location.origin}/players/${playerId}`, '_blank', 'noopener,noreferrer');
  }, []);

  const toggleRoleFilter = useCallback((role) => {
    setRoleFilter((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  }, []);

  const resetFilters = useCallback(() => {
    setSearch('');
    setRoleFilter([]);
    setTeamFilter('all');
    setCatFilter('all');
    setTierFilter('all');
    setSortMode('value_desc');
  }, []);

  if (loading) {
    return (
      <main className="container strategy-edit-page">
        <TopBar />
        <div className="se-loading glass">Caricamento strategia...</div>
      </main>
    );
  }

  if (notFound || !strategy) {
    return (
      <main className="container strategy-edit-page">
        <TopBar />
        <div className="se-loading glass">
          <AlertTriangle size={18} />
          Strategia non trovata.
          <button type="button" className="se-cancel-btn" onClick={() => navigate('/strategie')}>Torna alle strategie</button>
        </div>
      </main>
    );
  }

  // Un solo popover riusato per tutti i filtri (squadra/ruolo/etichetta/
  // fascia/ordinamento): qui si sceglie quale contenuto mostrare in base a
  // filterPickerState.type, senza duplicare il componente per ognuno.
  const filterPickerBuilders = {
    team: {
      options: teamOptions.map((t) => ({ value: t, label: t === 'all' ? 'Tutte le squadre' : t })),
      current: teamFilter, onSelect: setTeamFilter,
    },
    category: {
      options: CATEGORY_FILTERS.map((cat) => ({ value: cat, label: cat === 'all' ? 'Tutte le etichette' : getCategoryMeta(cat, customCategoryLabel).label })),
      current: catFilter, onSelect: setCatFilter,
    },
    tier: {
      options: TIER_FILTERS.map((t) => ({ value: t, label: t === 'all' ? 'Tutte le fasce' : t === 'none' ? 'Nessuna fascia' : `Fascia ${t}` })),
      current: tierFilter, onSelect: setTierFilter,
    },
    sort: {
      options: SORT_OPTIONS,
      current: sortMode, onSelect: setSortMode,
    },
  };
  const activeFilterPicker = filterPickerBuilders[filterPickerState?.type] || { options: [], current: null, onSelect: () => {} };

  return (
    <main className="container strategy-edit-page">
      <TopBar />

      <section className="se-hero glass">
        <button type="button" className="se-back-btn" onClick={() => navigate('/strategie')}>
          <ArrowLeft size={16} />
          Torna alle strategie
        </button>

        <div className="se-hero__row">
          <div className="se-hero__text">
            {isReadOnly && (
              <div className="se-hero__readOnlyBadge">
                <Globe2 size={13} />
                Strategia pubblica · sola lettura
              </div>
            )}
            {sharedCanEdit && (
              <div className="se-hero__readOnlyBadge se-hero__readOnlyBadge--shared">
                <Users size={13} />
                Strategia condivisa · puoi modificarla
              </div>
            )}
            <h1>{strategy.name}</h1>
            {strategy.description && <p>{strategy.description}</p>}
            {isForeignRoute && (
              <div className="se-hero__host">
                <UserRound size={13} />
                Creata da <strong>{ownerName || 'Utente'}</strong>
              </div>
            )}
          </div>
          <div className="se-hero__aside">
            {!isReadOnly && (
              <button type="button" className="se-rename-btn" onClick={() => setShowRename(true)}>
                <Pencil size={14} />
                Rinomina
              </button>
            )}
            <div className={`se-mode-toggle ${savingRolesMode ? 'is-saving' : ''}`} role="group" aria-label="Modalità ruoli">
              <button
                type="button"
                className={rolesMode === 'classic' ? 'is-active' : ''}
                aria-pressed={rolesMode === 'classic'}
                disabled={isReadOnly || savingRolesMode}
                onClick={() => handleRolesModeChange('classic')}
              >
                Classic
              </button>
              <button
                type="button"
                className={rolesMode === 'mantra' ? 'is-active' : ''}
                aria-pressed={rolesMode === 'mantra'}
                disabled={isReadOnly || savingRolesMode}
                onClick={() => handleRolesModeChange('mantra')}
              >
                Mantra
              </button>
            </div>
          </div>
        </div>

        <div className="se-hero__stats">
          <div className="se-stat-pill"><Tag size={13} /><strong>{summary.categories}</strong><span>Etichette</span></div>
          <div className="se-stat-pill"><Award size={13} /><strong>{summary.tiers}</strong><span>Fasce</span></div>
          <div className="se-stat-pill"><StickyNote size={13} /><strong>{summary.notes}</strong><span>Note</span></div>
          <div className="se-stat-pill"><Percent size={13} /><strong>{summary.maxSpends}</strong><span>Tetti di spesa</span></div>
          <div className="se-stat-pill se-stat-pill--accent"><strong>{summary.players}</strong><span>Giocatori coinvolti</span></div>
        </div>
      </section>

      <section className="se-filters glass">
        <button
          type="button"
          className="se-filters__toggle"
          onClick={() => setFiltersOpen((o) => !o)}
          aria-expanded={filtersOpen}
        >
          <span>
            <SlidersHorizontal size={15} />
            Filtri e ordinamento
            {(search || roleFilter.length > 0 || teamFilter !== 'all' || catFilter !== 'all' || tierFilter !== 'all' || sortMode !== 'value_desc') && (
              <i className="se-filters__dot" aria-hidden="true" />
            )}
          </span>
          <ChevronDown size={16} className={`se-filters__chevron ${filtersOpen ? 'is-open' : ''}`} />
        </button>

        <div className={`se-filters__body ${filtersOpen ? 'is-open' : ''}`}>
          <label className="se-search-wrap">
            <Search size={15} className="se-search-icon" />
            <input className="se-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca giocatore o squadra…" />
            {search && <button type="button" className="se-search-clear" onClick={() => setSearch('')}><X size={13} /></button>}
          </label>

          <div className="se-filters__roleline">
            <div className="se-role-chips" role="group" aria-label="Filtra per ruolo (multi-selezione)">
              <button
                type="button"
                className={`se-role-chip ${roleFilter.length === 0 ? 'is-active' : ''}`}
                onClick={() => setRoleFilter([])}
              >
                Tutti
              </button>
              {(isMantra ? MANTRA_ROLE_CHIPS : CLASSIC_ROLE_CHIPS).map((r) => (
                <button
                  key={r}
                  type="button"
                  aria-pressed={roleFilter.includes(r)}
                  className={`se-role-chip ${roleFilter.includes(r) ? 'is-active' : ''}`}
                  onClick={() => toggleRoleFilter(r)}
                >
                  {r}
                </button>
              ))}
            </div>

            <div className="se-filters__rolegroup">
              <button
                type="button"
                className="se-select se-select--btn"
                onClick={(e) => setFilterPickerState({ type: 'category', anchorRect: e.currentTarget.getBoundingClientRect() })}
              >
                <span>{catFilter === 'all' ? 'Tutte le etichette' : getCategoryMeta(catFilter, customCategoryLabel).label}</span>
                <ChevronDown size={14} />
              </button>

              <button
                type="button"
                className="se-select se-select--btn"
                onClick={(e) => setFilterPickerState({ type: 'team', anchorRect: e.currentTarget.getBoundingClientRect() })}
              >
                <span>{teamFilter === 'all' ? 'Tutte le squadre' : teamFilter}</span>
                <ChevronDown size={14} />
              </button>

              <button
                type="button"
                className="se-select se-select--btn"
                onClick={(e) => setFilterPickerState({ type: 'tier', anchorRect: e.currentTarget.getBoundingClientRect() })}
              >
                <span>{tierFilter === 'all' ? 'Tutte le fasce' : tierFilter === 'none' ? 'Nessuna fascia' : `Fascia ${tierFilter}`}</span>
                <ChevronDown size={14} />
              </button>

              <button
                type="button"
                className="se-select se-select--btn se-select--sort"
                onClick={(e) => setFilterPickerState({ type: 'sort', anchorRect: e.currentTarget.getBoundingClientRect() })}
              >
                <span>{SORT_OPTIONS.find((o) => o.value === sortMode)?.label || 'Ordina'}</span>
                <ChevronDown size={14} />
              </button>

              <button type="button" className="se-reset-btn" onClick={resetFilters}>
                <RotateCcw size={13} />
                Azzera filtri
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="se-shell glass">
        <div className="se-shell__bar">
          <strong>{totalItems === 0 ? 'Nessun giocatore trovato' : `${totalItems} giocatori`}</strong>
          <div className="se-pagination">
            <button type="button" className="se-pg-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}><ChevronLeft size={15} /></button>
            <span>Pag. {safePage} / {totalPages}</span>
            <button type="button" className="se-pg-btn" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}><ChevronRight size={15} /></button>
          </div>
        </div>

        <div className="se-table-wrap">
          <table className="se-table">
            <thead>
              <tr>
                <th>Squadra</th>
                <th>Giocatore</th>
                <th>Ruolo</th>
                <th>{backupMode ? 'Por riserva' : 'Gol'}</th>
                <th>Assist</th>
                <th>Valore</th>
                <th>FV</th>
                <th>MV</th>
                <th>Etichetta</th>
                <th>Fascia</th>
                <th>Tetto max</th>
                <th>Note</th>
                <th className="se-th--sticky-right">Scheda</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((player) => {
                const id = String(player.id);
                return (
                  <PlayerRow
                    key={id}
                    player={player}
                    category={String(localCats?.[id] || 'none')}
                    tier={Number(localTiers?.[id] || 0)}
                    note={localNotes?.[id] || ''}
                    maxSpend={Number(localMaxSpends?.[id] || 0)}
                    saving={savingId === id}
                    savingTier={savingTierId === id}
                    savingMaxSpend={savingMaxSpendId === id}
                    mantraRoleMap={mantraRoleMap}
                    rolesMode={rolesMode}
                    readOnly={isReadOnly}
                    backupMode={backupMode}
                    backupValue={localBackupGoalkeepers?.[id] || ''}
                    teamGoalkeepers={goalkeepersByTeam[player.team] || []}
                    customCategoryLabel={customCategoryLabel}
                    onBackupGoalkeeperChange={saveBackupGoalkeeper}
                    onOpenCategoryPicker={openCategoryPicker}
                    onOpenTierPicker={openTierPicker}
                    onOpenMaxSpendPicker={openMaxSpendPicker}
                    onOpenNotesEditor={openNotes}
                    onOpenDetail={openPlayerDetail}
                  />
                );
              })}
            </tbody>
          </table>

          <div className="sem-list">
            {pageRows.map((player) => {
              const id = String(player.id);
              return (
                <MobilePlayerCard
                  key={id}
                  player={player}
                  category={String(localCats?.[id] || 'none')}
                  tier={Number(localTiers?.[id] || 0)}
                  note={localNotes?.[id] || ''}
                  maxSpend={Number(localMaxSpends?.[id] || 0)}
                  saving={savingId === id}
                  savingTier={savingTierId === id}
                  savingMaxSpend={savingMaxSpendId === id}
                  mantraRoleMap={mantraRoleMap}
                  rolesMode={rolesMode}
                  readOnly={isReadOnly}
                  backupMode={backupMode}
                  backupValue={localBackupGoalkeepers?.[id] || ''}
                  teamGoalkeepers={goalkeepersByTeam[player.team] || []}
                  customCategoryLabel={customCategoryLabel}
                  onBackupGoalkeeperChange={saveBackupGoalkeeper}
                  onOpenCategoryPicker={openCategoryPicker}
                  onOpenTierPicker={openTierPicker}
                  onOpenMaxSpendPicker={openMaxSpendPicker}
                  onOpenNotesEditor={openNotes}
                  onOpenDetail={openPlayerDetail}
                />
              );
            })}
          </div>

          {pageRows.length === 0 && <div className="se-empty">Nessun giocatore con i filtri selezionati.</div>}
        </div>
      </section>

      <CategoryPopover
        open={Boolean(pickerState)}
        anchorRect={pickerState?.anchorRect || null}
        playerName={pickerState?.playerName || '—'}
        currentCategory={pickerState?.currentCategory || 'none'}
        saving={savingId === String(pickerState?.playerId || '')}
        onSelect={(cat) => handleCategorySelect(pickerState.playerId, cat)}
        onClose={() => setPickerState(null)}
        customLabel={customCategoryLabel}
        canRenameCustom={canRenameCustomCategory}
        onRequestRenameCustom={openRenameCustomCategory}
      />

      {renamingCustomCategory && (
        <RenameCustomCategoryModal
          initialLabel={customCategoryLabel}
          onClose={() => setRenamingCustomCategory(false)}
          onSubmit={submitRenameCustomCategory}
          saving={savingCustomCategoryLabel}
        />
      )}

      <TierPopover
        open={Boolean(tierPickerState)}
        anchorRect={tierPickerState?.anchorRect || null}
        playerName={tierPickerState?.playerName || '—'}
        currentTier={tierPickerState?.currentTier || 0}
        saving={savingTierId === String(tierPickerState?.playerId || '')}
        onSelect={(tier) => handleTierSelect(tierPickerState.playerId, tier)}
        onClose={() => setTierPickerState(null)}
      />

      <MaxSpendPopover
        open={Boolean(maxSpendPickerState)}
        anchorRect={maxSpendPickerState?.anchorRect || null}
        playerName={maxSpendPickerState?.playerName || '—'}
        currentPercent={maxSpendPickerState?.currentPercent || 0}
        saving={savingMaxSpendId === String(maxSpendPickerState?.playerId || '')}
        onSelect={(percent) => handleMaxSpendSelect(maxSpendPickerState.playerId, percent)}
        onClose={() => setMaxSpendPickerState(null)}
      />

      <FilterSelectPopover
        open={Boolean(filterPickerState)}
        anchorRect={filterPickerState?.anchorRect || null}
        options={activeFilterPicker.options}
        current={activeFilterPicker.current}
        onSelect={(value) => { activeFilterPicker.onSelect(value); setFilterPickerState(null); }}
        onClose={() => setFilterPickerState(null)}
      />

      {notesState && typeof document !== 'undefined' && createPortal(
        <div className="se-notes-layer" onClick={() => setNotesState(null)}>
          <div className="se-notes-modal glass" role="dialog" aria-label={`Note per ${notesState.playerName}`} onClick={(e) => e.stopPropagation()}>
            <div className="se-notes-head">
              <div>
                <strong>{notesState.playerName}</strong>
                <span>Nota personale per questa strategia</span>
              </div>
              <button type="button" className="se-notes-close" onClick={() => setNotesState(null)}><X size={16} /></button>
            </div>
            <textarea className="se-notes-textarea" defaultValue={notesState.value} placeholder="Scrivi qui la tua nota…" rows={7} />
            <div className="se-notes-actions">
              <button type="button" className="se-cancel-btn" onClick={() => setNotesState(null)}>Annulla</button>
              <button
                type="button"
                className="se-save-btn"
                onClick={() => {
                  const ta = document.querySelector('.se-notes-textarea');
                  saveNotes(ta?.value || '');
                }}
              >
                Salva nota
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showRename && (
        <RenameModal
          initialName={strategy.name}
          initialDescription={strategy.description}
          onClose={() => setShowRename(false)}
          onSubmit={handleRenameSubmit}
          saving={savingMeta}
        />
      )}
    </main>
  );
}
